import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { type FormEvent, useState } from 'react'
import { CardAPI } from '@certi/shared/api/payments/CardAPI'

interface AddCardFormProps {
  accessToken: string
  onSuccess: () => void
  onCancel: () => void
}

/**
 * Form for adding a new payment card using Stripe Elements
 */
export function AddCardForm({ accessToken, onSuccess, onCancel }: AddCardFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [cardholderName, setCardholderName] = useState('')

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setLoading(true)
    setError(null)

    try {
      // 1. Create SetupIntent on backend
      let setupIntentResponse
      try {
        setupIntentResponse = await CardAPI.createSetupIntent(accessToken)
      } catch (err: any) {
        console.error('Failed to create setup intent:', err)
        throw new Error('Payment system is currently unavailable. Please try again later or contact support.')
      }
      const { clientSecret } = setupIntentResponse

      // 2. Get CardElement
      const cardElement = elements.getElement(CardElement)
      if (!cardElement) {
        throw new Error('Card element not found')
      }

      // 3. Confirm card setup with Stripe
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              name: cardholderName || undefined,
            },
          },
        }
      )

      if (stripeError) {
        console.error('Stripe error:', stripeError)
        setError(stripeError.message || 'Unable to add card. Please check your card details and try again.')
        setLoading(false)
        return
      }

      if (setupIntent?.status === 'succeeded') {
        await CardAPI.confirmSetupIntent(setupIntent.id, accessToken)
        onSuccess()
      }
    } catch (err: any) {
      console.error('Failed to add card:', err)
      setError('Unable to add card. Please try again or contact support.')
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Cardholder Name
        </label>
        <input
          type="text"
          value={cardholderName}
          onChange={(e) => setCardholderName(e.target.value)}
          placeholder="Name on card"
          className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600 focus:border-transparent"
        />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Card Details
        </label>
        <div className="p-4 border border-gray-300 rounded-lg bg-white">
          <CardElement
            options={{
              hidePostalCode: true,
              style: {
                base: {
                  fontSize: '16px',
                  color: '#1f2937',
                  '::placeholder': {
                    color: '#9ca3af',
                  },
                },
                invalid: {
                  color: '#ef4444',
                },
              },
            }}
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={!stripe || loading}
          className="btn btn-primary flex-1"
        >
          {loading ? 'Adding card...' : 'Add card'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={loading}
          className="btn btn-secondary flex-1"
        >
          Cancel
        </button>
      </div>
    </form>
  )
}
